import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Sprout, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const projectId = params.get("project");
  const [project, setProject] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!projectId) return;
    axios.get(`${API}/projects/public`)
      .then((r) => setProject(r.data.find((p) => p.id === projectId) || null))
      .catch(() => {});
  }, [projectId]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email.trim(), password);
      if (project) {
        localStorage.setItem("activeProject", JSON.stringify({ id: project.id, name: project.name }));
      }
      toast.success(project ? `Welcome to ${project.name}` : "Welcome back");
      nav("/app");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const quickFill = (em, pw) => { setEmail(em); setPassword(pw); };

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      <div className="hidden md:block relative">
        <img src={project?.cover_image || "https://images.unsplash.com/photo-1560198927-b31ef30c5867?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwxfHxmYXJtbGFuZCUyMGFlcmlhbCUyMHZpZXd8ZW58MHx8fHwxNzg2NjE3MjczfDA&ixlib=rb-4.1.0&q=85"} alt="Farm" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-primary/40" />
        <div className="relative z-10 h-full flex flex-col justify-end p-10 text-primary-foreground">
          <div className="text-xs uppercase tracking-[0.25em] opacity-80">Roots by Sanjeevani Farms</div>
          <div className="heading text-3xl mt-2 max-w-md">
            {project ? project.name : "Structured operations for managed farmland."}
          </div>
          {project?.location && <div className="text-sm mt-2 opacity-90">{project.location}</div>}
        </div>
      </div>
      <div className="flex items-center justify-center p-8 bg-background">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="login-form">
          <Link to="/" className="flex items-center gap-2 mb-8">
            <div className="h-9 w-9 rounded-md bg-primary text-primary-foreground flex items-center justify-center"><Sprout size={18} /></div>
            <span className="heading font-semibold text-sm sm:text-base">Roots by Sanjeevani Farms</span>
          </Link>
          {project && (
            <Link to="/" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground mb-4" data-testid="back-to-projects">
              <ArrowLeft size={12} /> All projects
            </Link>
          )}
          {project && (
            <div className="mb-6 p-4 rounded-md border border-primary/30 bg-primary/5" data-testid="project-context-banner">
              <div className="text-[10px] uppercase tracking-[0.2em] text-primary font-semibold">Signing in to</div>
              <div className="heading text-lg font-semibold mt-1">{project.name}</div>
              <div className="text-xs text-muted-foreground">{project.location}</div>
            </div>
          )}
          <h1 className="heading text-3xl font-semibold">Welcome back</h1>
          <p className="text-sm text-muted-foreground mt-1">Sign in to manage your plots.</p>

          <div className="space-y-4 mt-8">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email-input" />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password-input" />
            </div>
            <Button type="submit" className="w-full" disabled={busy} data-testid="login-submit-btn">
              {busy ? "Signing in…" : "Sign in"}
            </Button>
          </div>
          <div className="mt-4 text-xs text-right">
            <Link to="/forgot-password" className="text-muted-foreground hover:text-primary" data-testid="forgot-password-link">Forgot password?</Link>
          </div>
          <div className="mt-4 text-sm text-muted-foreground">
            No account? <Link to={project ? `/register?project=${project.id}` : "/register"} className="text-primary font-medium">Create one</Link>
          </div>
          <div className="mt-6 border-t border-border pt-4">
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-2">Demo accounts</div>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <button type="button" className="border border-border rounded-md p-2 text-left hover:bg-secondary" onClick={() => quickFill("customer@farmland.demo", "Demo@123")} data-testid="demo-customer-btn">Customer</button>
              <button type="button" className="border border-border rounded-md p-2 text-left hover:bg-secondary" onClick={() => quickFill("worker@farmland.demo", "Demo@123")} data-testid="demo-worker-btn">Field worker</button>
              <button type="button" className="border border-border rounded-md p-2 text-left hover:bg-secondary" onClick={() => quickFill("manager@farmland.demo", "Demo@123")} data-testid="demo-manager-btn">Manager</button>
              <button type="button" className="border border-border rounded-md p-2 text-left hover:bg-secondary" onClick={() => quickFill("suresh@digitalbrains.in", "Farmland@2026")} data-testid="demo-admin-btn">Super admin</button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
